import { ISplitOptions } from "../../models/text/ISplitOptions";

export type SegmentedTextPart = {
  text: string;
  start: number;
  end: number;
  noSpaceBefore: boolean;
};

const graphemeSegmenter =
  typeof Intl !== "undefined" && "Segmenter" in Intl
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : null;

export function splitIntoSegments(text: string, options: ISplitOptions): SegmentedTextPart[] {
  if (options.segment !== "visual") {
    return splitByWhitespace(text);
  }

  return splitVisualText(text);
}

export function splitIntoGraphemes(text: string, options: ISplitOptions): string[] {
  if (options.segment !== "visual" || !text) {
    return Array.from(text);
  }

  if (!graphemeSegmenter) {
    return Array.from(text);
  }

  return Array.from(graphemeSegmenter.segment(text), ({ segment }) => segment);
}

function splitByWhitespace(text: string): SegmentedTextPart[] {
  const parts: SegmentedTextPart[] = [];
  const wordRe = /\S+/g;
  let match: RegExpExecArray | null;

  while ((match = wordRe.exec(text)) !== null) {
    pushWordWithHyphens(match[0], match.index, parts);
  }

  return parts;
}

// Splits a word at line-breakable hyphens (U+002D, U+2010), keeping the hyphen
// with the left part. Only splits at interior hyphens (not leading/trailing).
function pushWordWithHyphens(word: string, wordStart: number, parts: SegmentedTextPart[]): void {
  let segStart = 0;
  for (let i = 1; i < word.length - 1; i++) {
    const ch = word[i];
    if (ch === '-' || ch === '‐') {
      const seg = word.slice(segStart, i + 1);
      const absStart = wordStart + segStart;
      parts.push({
        text: seg,
        start: absStart,
        end: absStart + seg.length,
        noSpaceBefore: parts.length > 0 && absStart === parts[parts.length - 1].end,
      });
      segStart = i + 1;
    }
  }
  if (segStart < word.length) {
    const seg = word.slice(segStart);
    const absStart = wordStart + segStart;
    parts.push({
      text: seg,
      start: absStart,
      end: absStart + seg.length,
      noSpaceBefore: parts.length > 0 && absStart === parts[parts.length - 1].end,
    });
  }
}

function splitVisualText(text: string): SegmentedTextPart[] {
  const graphemes = splitIntoIndexedGraphemes(text);
  const parts: SegmentedTextPart[] = [];
  let current: { text: string; start: number; end: number } | null = null;

  const flush = () => {
    if (!current) return;
    parts.push({
      text: current.text,
      start: current.start,
      end: current.end,
      noSpaceBefore: parts.length > 0 && current.start === parts[parts.length - 1].end,
    });
    current = null;
  };

  graphemes.forEach(({ segment, index }) => {
    if (/\s/u.test(segment)) {
      flush();
      return;
    }

    const kind = classifyVisualSegment(segment);
    const end = index + segment.length;

    if (kind === "visual") {
      // Closing punctuation attaches to the preceding word/segment (no space before it).
      if (isClosingPunct(segment)) {
        if (current) {
          current.text += segment;
          current.end = end;
          return;
        }
        if (parts.length > 0 && index === parts[parts.length - 1].end) {
          parts[parts.length - 1].text += segment;
          parts[parts.length - 1].end = end;
          return;
        }
      }
      flush();
      parts.push({
        text: segment,
        start: index,
        end,
        noSpaceBefore: parts.length > 0 && index === parts[parts.length - 1].end,
      });
      return;
    }

    const endsWithHyphen =
      current !== null && (current.text.endsWith('-') || current.text.endsWith('‐'));

    if (!current || current.end !== index || endsWithHyphen) {
      flush();
      current = { text: segment, start: index, end };
      return;
    }

    current.text += segment;
    current.end = end;
  });

  flush();
  return parts;
}

function splitIntoIndexedGraphemes(text: string): { segment: string; index: number }[] {
  if (!graphemeSegmenter) {
    const result: { segment: string; index: number }[] = [];
    let offset = 0;
    Array.from(text).forEach((segment) => {
      result.push({ segment, index: offset });
      offset += segment.length;
    });
    return result;
  }

  return Array.from(graphemeSegmenter.segment(text), ({ segment, index }) => ({
    segment,
    index,
  }));
}

function isClosingPunct(s: string): boolean {
  return /[\)\]\}）》」』】〕〗〙〛〉》〞〟›»"'\u3001\u3002\uFF0C\uFF0E\uFF01\uFF1F\uFF1A\uFF1B\u2026]/u.test(
    s,
  );
}

function classifyVisualSegment(segment: string): "word" | "visual" {
  if (/\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}/u.test(segment)) {
    return "visual";
  }

  if (/[\p{Script=Latin}\p{Script=Cyrillic}\p{Script=Greek}\p{Script=Hangul}\p{Number}]/u.test(segment)) {
    return "word";
  }

  if (/[\p{Mark}\p{Connector_Punctuation}\p{Dash_Punctuation}'’._+#&/@]/u.test(segment)) {
    return "word";
  }

  return "visual";
}
